import { Badge, Button, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPencil, IconScale } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { GovernanceSetupModal } from 'src/Components/Modals/GovernanceSetupModal';
import { PageHeader } from 'src/Components/Navigation/PageHeader';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import {
  AssetGovernanceFields,
  ManagementMode,
  RentalDeclarationRegime,
  ShareType,
  SpecialStatus,
  SyndicLibreType,
  TaxRegime,
} from 'src/types/governance.types';
import { LEGAL_STRUCTURE_LABELS } from 'src/Utils/legalStructureVocabulary';

import styles from './GovernanceSection.module.scss';

const TAX_REGIME_LABELS: Record<TaxRegime, string> = {
  [TaxRegime.IR]: 'Impôt sur le Revenu (IR)',
  [TaxRegime.IS]: 'Impôt sur les Sociétés (IS)',
};

const RENTAL_DECLARATION_REGIME_LABELS: Record<RentalDeclarationRegime, string> = {
  [RentalDeclarationRegime.REEL]: 'Régime réel',
  [RentalDeclarationRegime.MICRO_FONCIER]: 'Micro-foncier',
};

const SHARE_TYPE_LABELS: Record<ShareType, string> = {
  [ShareType.PERCENTAGE]: 'Pourcentages (%)',
  [ShareType.TANTIEMES]: 'Tantièmes',
  [ShareType.PARTS_SOCIALES]: 'Parts sociales',
};

const getManagementModeBadge = (
  mode?: ManagementMode,
  type?: SyndicLibreType
): { title: string; description: string } | null => {
  if (mode !== ManagementMode.SYNDIC_LIBRE) return null;
  if (type === SyndicLibreType.BENEVOLE) {
    return { title: '🤝 Syndic bénévole', description: 'Géré par un copropriétaire élu' };
  }
  if (type === SyndicLibreType.COOPERATIF) {
    return {
      title: '🤝 Syndic coopératif',
      description: 'Géré collégialement par le conseil syndical',
    };
  }
  return { title: '🤝 Syndic libre', description: 'Géré par les copropriétaires' };
};

function getGovernanceFields(
  asset: Record<string, unknown> | undefined
): AssetGovernanceFields | undefined {
  if (!asset) return undefined;
  return asset as unknown as AssetGovernanceFields;
}

const GovernanceSection = () => {
  const { t } = useTranslation();
  const { data: asset } = useFetchAssetDetails();
  const [modalOpen, modalActions] = useDisclosure(false);

  const governance = getGovernanceFields(asset as unknown as Record<string, unknown>);
  const isConfigured = !!governance?.legalStructure;

  return (
    <div>
      <PageHeader
        label={t('admin.governance.title', 'Gouvernance juridique')}
        rightElement={
          isConfigured ? (
            <Button
              variant="light"
              leftSection={<IconPencil size={16} />}
              onClick={modalActions.open}
            >
              {t('common.edit', 'Modifier')}
            </Button>
          ) : undefined
        }
      />

      {isConfigured ? (
        <div className={styles.container}>
          {(() => {
            const badge = getManagementModeBadge(
              governance.managementMode,
              governance.syndicLibreType
            );
            if (!badge) return null;
            return (
              <div className={styles.inputRow}>
                <span className={styles.inputLabel}>
                  {t('admin.governance.managementMode', 'Mode de gestion')}
                </span>
                <Badge color="teal" variant="light" size="lg" radius="sm">
                  {badge.title} — {badge.description}
                </Badge>
              </div>
            );
          })()}
          <div className={styles.inputRow}>
            <span className={styles.inputLabel}>
              {t('admin.governance.legalStructure', 'Structure juridique')}
            </span>
            <span>{LEGAL_STRUCTURE_LABELS[governance.legalStructure!]}</span>
          </div>
          {governance.taxRegime && (
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>
                {t('admin.governance.taxRegime', 'Régime fiscal')}
              </span>
              <span>{TAX_REGIME_LABELS[governance.taxRegime]}</span>
            </div>
          )}
          {governance.specialStatuses && governance.specialStatuses.length > 0 && (
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>
                {t('admin.governance.specialStatuses', 'Statuts speciaux')}
              </span>
              <span>
                {governance.specialStatuses
                  .map((s: SpecialStatus) =>
                    s === SpecialStatus.LMNP ? 'Location Meublée (LMNP)' : 'Monument Historique'
                  )
                  .join(', ')}
              </span>
            </div>
          )}
          <div className={styles.inputRow}>
            <span className={styles.inputLabel}>
              {t('admin.governance.dismemberment', 'Démembrement')}
            </span>
            <span>{governance.hasDismemberment ? 'Oui' : 'Non'}</span>
          </div>
          {governance.rentalDeclarationRegime && (
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>
                {t('admin.governance.rentalDeclarationRegime', 'Fiscalité locative')}
              </span>
              <span>
                {RENTAL_DECLARATION_REGIME_LABELS[governance.rentalDeclarationRegime]}
                {governance.rentalDeclarationRegime === RentalDeclarationRegime.REEL &&
                  governance.defaultLocativeShare != null &&
                  ` — ${governance.defaultLocativeShare}% par défaut`}
              </span>
            </div>
          )}
          {governance.shareType && (
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>
                {t('admin.governance.shares', 'Répartition')}
              </span>
              <span>
                {governance.shareType === ShareType.PERCENTAGE
                  ? 'Pourcentages (%)'
                  : governance.totalShares
                    ? `${governance.totalShares} ${SHARE_TYPE_LABELS[governance.shareType]}`
                    : SHARE_TYPE_LABELS[governance.shareType]}
              </span>
            </div>
          )}
        </div>
      ) : (
        <Stack gap="md" mt="md">
          <Text c="dimmed" size="sm">
            {t(
              'admin.governance.description',
              'Configurez la structure juridique de votre propriété pour activer la répartition des parts et des droits.'
            )}
          </Text>
          <Button
            variant="light"
            leftSection={<IconScale size={16} />}
            onClick={modalActions.open}
            style={{ alignSelf: 'flex-start' }}
          >
            {t('admin.governance.configure', 'Configurer la structure juridique')}
          </Button>
        </Stack>
      )}

      <GovernanceSetupModal open={modalOpen} onClose={modalActions.close} governance={governance} />
    </div>
  );
};

export default GovernanceSection;
